const HolisticReportCardPreview = ({ data, student, userProfile, theme }) => {
  const instituteInfo = userProfile?.reportCardInstituteDefaults || {};
  
  // Default theme if not provided
  const currentTheme = theme || {
    primaryHex: '#4F46E5',
    secondary: '#EC4899',
  };

  // Convert hex to RGB for table header background with opacity
  const hexToRgba = (hex, alpha = 0.2) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const headerBgStyle = { backgroundColor: currentTheme.primaryHex };
  const sectionHeaderStyle = { color: currentTheme.primaryHex, borderBottomColor: currentTheme.secondary };
  const tableHeaderStyle = { backgroundColor: hexToRgba(currentTheme.primaryHex, 0.15) };
  const accentTextStyle = { color: currentTheme.primaryHex };

  return (
    <div className="p-6 border rounded-lg bg-white shadow-lg font-sans relative overflow-hidden">
      <div className="relative z-10">
        {/* Header */}
        <div className="rounded-t-lg p-4 mb-4 -mx-6 -mt-6 text-white text-center" style={headerBgStyle}>
          <h2 className="text-2xl font-bold mb-1">{userProfile?.coachingName || 'Institute Name'}</h2>
          <div className="text-xs opacity-90">
            {instituteInfo.address && <p>{instituteInfo.address}</p>}
            <p>
              {instituteInfo.institutePhone && <span>Phone: {instituteInfo.institutePhone}</span>}
              {instituteInfo.institutePhone && instituteInfo.instituteEmail && <span className="mx-2">|</span>}
              {instituteInfo.instituteEmail && <span>Email: {instituteInfo.instituteEmail}</span>}
            </p>
            {instituteInfo.affiliationNumber && <p>Affiliation No: {instituteInfo.affiliationNumber}</p>}
          </div>
        </div>

        {/* Student Details */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-2 border-b-2 pb-1" style={sectionHeaderStyle}>1. Student Details</h4>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <div><strong>Name:</strong> {student?.name || student?.displayName}</div>
            <div><strong>Class:</strong> {data.class}</div>
            <div><strong>Section:</strong> {data.section}</div>
            <div><strong>Roll Number:</strong> {data.rollNumber}</div>
            <div><strong>Date of Birth:</strong> {data.dateOfBirth}</div>
            <div><strong>Academic Session:</strong> {data.academicSession}</div>
          </div>
        </div>

        {/* Academic Performance */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-2 border-b-2 pb-1" style={sectionHeaderStyle}>2. Academic Performance</h4>
          {data.reportType === 'exam' ? (
            <div>
              <h5 className="text-md font-semibold mb-2">Exam: {data.examName}</h5>
              <table className="w-full border-collapse border border-gray-300 text-sm">
                <thead style={tableHeaderStyle}>
                  <tr>
                    <th className="border p-2 font-semibold" style={accentTextStyle}>Subject</th>
                    <th className="border p-2 font-semibold" style={accentTextStyle}>Marks Obtained</th>
                    <th className="border p-2 font-semibold" style={accentTextStyle}>Maximum Marks</th>
                    <th className="border p-2 font-semibold" style={accentTextStyle}>Grade</th>
                    <th className="border p-2 font-semibold" style={accentTextStyle}>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {data.subjects.map((subject, index) => (
                    <tr key={index} className="even:bg-gray-50">
                      <td className="border p-2">{subject.name}</td>
                      <td className="border p-2 text-center">{subject.marks}</td>
                      <td className="border p-2 text-center">{subject.maxMarks}</td>
                      <td className="border p-2 text-center">{subject.grade}</td>
                      <td className="border p-2">{subject.remarks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div>
              <table className="w-full border-collapse border border-gray-300 text-sm">
                <thead style={tableHeaderStyle}>
                  <tr>
                    <th className="border p-2 font-semibold" style={accentTextStyle}>Subject</th>
                    {data.finalReport.exams.map((exam, index) => (
                      <th key={index} className="border p-2 font-semibold" style={accentTextStyle}>{exam.name} ({exam.maxMarks})</th>
                    ))}
                    <th className="border p-2 font-semibold" style={accentTextStyle}>Total Marks</th>
                  </tr>
                </thead>
                <tbody>
                  {data.finalReport.subjects.map((subject, subIndex) => (
                    <tr key={subIndex} className="even:bg-gray-50">
                      <td className="border p-2">{subject.name}</td>
                      {data.finalReport.exams.map((exam, examIndex) => (
                        <td key={examIndex} className="border p-2 text-center">{subject.marks[examIndex]}</td>
                      ))}
                      <td className="border p-2 text-center font-semibold">{subject.marks.reduce((a, b) => Number(a) + Number(b), 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Overall Academic Summary */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-2 border-b-2 pb-1" style={sectionHeaderStyle}>3. Overall Academic Summary</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2 text-sm">
            <div><strong>Total Marks Obtained:</strong> {data.summary.totalMarksObtained}</div>
            <div><strong>Total Maximum Marks:</strong> {data.summary.totalMaximumMarks}</div>
            <div className="font-semibold" style={accentTextStyle}><strong>Percentage:</strong> {data.summary.percentage}%</div>
            <div><strong>Overall Grade:</strong> {data.summary.overallGrade}</div>
            <div><strong>Rank:</strong> {data.summary.rank}</div>
          </div>
        </div>

        {/* Co-Scholastic Areas */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-2 border-b-2 pb-1" style={sectionHeaderStyle}>4. Co-Scholastic Areas</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2 text-sm">
            <div><strong>Discipline:</strong> {data.coScholastic.discipline}</div>
            <div><strong>Communication Skills:</strong> {data.coScholastic.communicationSkills}</div>
            <div><strong>Participation:</strong> {data.coScholastic.participation}</div>
            <div><strong>Teamwork:</strong> {data.coScholastic.teamwork}</div>
            <div><strong>Leadership:</strong> {data.coScholastic.leadership}</div>
            <div><strong>Creativity:</strong> {data.coScholastic.creativity}</div>
          </div>
        </div>

        {/* Skills */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-2 border-b-2 pb-1" style={sectionHeaderStyle}>5. Skills</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2 text-sm">
            <div><strong>Critical Thinking:</strong> {data.skills.criticalThinking}</div>
            <div><strong>Problem Solving:</strong> {data.skills.problemSolving}</div>
            <div><strong>Analytical Skills:</strong> {data.skills.analyticalSkills}</div>
            <div><strong>Collaboration:</strong> {data.skills.collaboration}</div>
            <div><strong>Initiative:</strong> {data.skills.initiative}</div>
          </div>
        </div>

        {/* Attendance */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-2 border-b-2 pb-1" style={sectionHeaderStyle}>6. Attendance</h4>
          <div className="grid grid-cols-3 gap-x-8 gap-y-2 text-sm">
            <div><strong>Total Working Days:</strong> {data.attendance.totalWorkingDays}</div>
            <div><strong>Days Present:</strong> {data.attendance.daysPresent}</div>
            <div className="font-semibold"><strong>Percentage:</strong> {data.attendance.percentage}%</div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t-2 border-gray-200">
          <div className="flex justify-between text-sm">
            <div className="w-1/2 pr-4">
              <h4 className="text-md font-semibold mb-2 text-gray-800">Teacher's Remarks</h4>
              <p className="text-gray-600 italic">{data.footer.teacherRemarks}</p>
              <div className="mt-8"><strong>{data.footer.teacherName}</strong></div>
              <div className="text-xs text-gray-500">(Teacher's Signature)</div>
            </div>
            <div className="w-1/2 pl-4 text-right">
              <h4 className="text-md font-semibold mb-2 text-gray-800">Principal's Remarks</h4>
              <p className="text-gray-600 italic">{data.footer.principalRemarks}</p>
              <div className="mt-8"><strong>{data.footer.principalName}</strong></div>
              <div className="text-xs text-gray-500">(Principal's Signature)</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HolisticReportCardPreview;
